import React from 'react';
import { RoleName, UserStatus } from '../domain';
import { Redirect } from '../lib/components';
import { routeMatcher, RouteMatcher } from '../lib/components/router/routeMatcher';
import { logger, toInt } from '../lib/helpers';
import { StudentClassroomScene } from '../scenes/classroom/student/StudentClassroomScene';
import { TeacherClassroomScene } from '../scenes/classroom/teacher/TeacherClassroomScene';
import { LanguageListComponent } from '../scenes/content/language-list/LanguageListComponent';
import { X1LanguageComponent } from '../scenes/content/x1/X1LanguageComponent';
import { X2AgeCategoryComponent } from '../scenes/content/x2/X2AgeCategoryComponent';
import { X3LevelComponent } from '../scenes/content/x3/X3LevelComponent';
import { X4UnitComponent } from '../scenes/content/x4/X4UnitComponent';
import { X5LessonComponent } from '../scenes/content/x5/X5LessonComponent';
import { X6PageComponent } from '../scenes/content/x6/X6PageComponent';
import { X7PushableBlockComponent } from '../scenes/content/x7/X7PushableBlockComponent';
import { DashboardPage } from '../scenes/dashboard/DashboardPage';
import { LoginScene } from '../scenes/login/LoginScene';
import { NotAuthorizedScene } from '../scenes/NotAuthorizedScene';
import { NotFoundScene } from '../scenes/NotFoundScene';
import { AdminClassroomScene } from '../scenes/schedule/administrator/AdminClassroomScene';
import { ClassroomDetailsScene } from '../scenes/schedule/administrator/ClassroomDetailsScene';
import StudentScheduleScene from '../scenes/schedule/student/StudentScheduleScene';
import { TeacherScheduleScene } from '../scenes/schedule/teacher/TeacherScheduleScene';
import { TestScene } from '../scenes/TestScene';
import { TimeZoneComponent } from '../scenes/timezone/TimeZoneComponent';
import { UserAddScene } from '../scenes/user/add/UserAddScene';
import { UserEditScene } from '../scenes/user/edit/UserEditScene';
import { UserSearchScene } from '../scenes/user/search/UserSearchScene';
import { UserViewScene } from '../scenes/user/view/UserViewScene';
import { CurrentUserScene } from '../scenes/user/whoami/CurrentUserScene';
import { UserStatusUnknownScene } from '../scenes/UserStatusUnknownScene';
import { UserStatusUnreachableScene } from '../scenes/UserStatusUnreachableScene';
import { Welcome } from '../scenes/Welcome';
import { routes } from './Routes';
import { TeacherWorkDayScene } from 'scenes/classroom/teacher/TeacherWorkDayScene';
import { TeacherLessonsScene } from 'scenes/classroom/teacher/TeacherLessonsScene';
import { TeacherStudentsScene } from 'scenes/classroom/teacher/TeacherStudentsScene';
import StudentHomeworkScene from '../scenes/homework/student/StudentHomeworkScene';
import StudentHomeworkListScene from 'scenes/homeworkList/StudentHomeworkListScene';
import EssayStudentScene from 'scenes/essay/student/EssayStudentScene';
import { EssayTeacherScene } from 'scenes/essay/teacher/EssayTeacherScene';
import { EssayTeacherListScene } from 'scenes/essay/teacher/EssayTeacherListScene';
import EssayStudentListScene from 'scenes/essay/student/EssayStudentListScene';
import { ExamTeacherListScene } from 'scenes/exam/teacher/ExamTeacherListScene';
import ExamStudentListScene from 'scenes/exam/student/ExamStudentListScene';
import { ExamStudentkScene } from 'scenes/exam/student/ExamStudentScene';
import { ExamTeacherScene } from 'scenes/exam/teacher/ExamTeacherScene';
import RegistrationListScene from 'scenes/registration/RegistrationList';
import { StudentProfile } from 'scenes/student-profile/StudentProfile';

type RouteParams = ReadonlyMap<string, string>;

interface RouteChildProvided {
  userStatus: UserStatus,
  route: string;
  params: RouteParams;
}

type RouteRenderFn = (provided: RouteChildProvided) => React.ReactNode;

interface RouteEntry {
  readonly route: string;
  readonly matcher: RouteMatcher;
  readonly renderFn: RouteRenderFn;
}

function component(Component: React.ComponentType<{}>): RouteRenderFn {
  return () => {
    return <Component />;
  };
}

function componentWithId(Component: React.ComponentType<{ id: number }>): RouteRenderFn {
  return (provided: RouteChildProvided) => {
    const idParam = provided.params.get('id');
    if (idParam === undefined)
      return <NotFoundScene />;
    const id = toInt(idParam);
    if (id === null)
      return <NotFoundScene />;
    return <Component key={id} id={id} />;
  };
}

function authenticated(renderFn: RouteRenderFn): RouteRenderFn {
  return (provided: RouteChildProvided) => {
    const { userStatus } = provided;
    if (userStatus.isUnknown)
      return <UserStatusUnknownScene />;
    if (userStatus.isServerUnreachable)
      return <UserStatusUnreachableScene />;
    if (userStatus.isLoggedOut)
      return <Redirect to={R.Login} />;
    if (userStatus.isLoggedIn)
      return renderFn(provided);

    return unreachableCode(provided);
  };
}

function requiresRole(renderFn: RouteRenderFn,
  roles: readonly RoleName[]): RouteRenderFn {
  return (provided: RouteChildProvided) => {
    const { userStatus } = provided;
    if (userStatus.isUnknown)
      return <UserStatusUnknownScene />;
    if (userStatus.isServerUnreachable)
      return <UserStatusUnreachableScene />;
    if (userStatus.isLoggedOut)
      return <Redirect to={R.Login} />;
    if (userStatus.isLoggedIn) {
      const roleNames = userStatus.roleNames;
      for (let role of roles)
        if (roleNames.has(role))
          return renderFn(provided);
      return <NotAuthorizedScene />;
    }

    return unreachableCode(provided);
  };
}

function entry(route: string, renderFn: RouteRenderFn): RouteEntry {
  return {
    route: route,
    matcher: routeMatcher(route),
    renderFn: renderFn,
  };
}

const R = routes;

const unreachableCode: RouteRenderFn = (provided: RouteChildProvided) => {
  logger.error('Unrecognized routing for route', provided.route);
  return (
    <div className="alert alert-danger">
      Unreachable code detected. .Check your router configuration.
    </div>
  );
};

const index = entry('/', (provided: RouteChildProvided) => {
  const { userStatus } = provided;
  if (userStatus.isUnknown)
    return <UserStatusUnknownScene />;
  if (userStatus.isServerUnreachable)
    return <UserStatusUnreachableScene />;
  if (userStatus.isLoggedOut)
    return <Redirect to={R.Login} />;
  if (userStatus.isLoggedIn)
    return <Redirect to={R.Dashboard} />;

  return unreachableCode(provided);
});

const login = entry(R.Login, (provided: RouteChildProvided) => {
  const { userStatus } = provided;
  if (userStatus.isUnknown)
    return <UserStatusUnknownScene />;
  if (userStatus.isServerUnreachable)
    return <UserStatusUnreachableScene />;
  if (userStatus.isLoggedOut)
    return <LoginScene />;
  if (userStatus.isLoggedIn)
    return <Redirect to={R.Dashboard} />;

  return unreachableCode(provided);
});

export const routeEntries: readonly RouteEntry[] = [
  index,
  login,
  entry(R.Dashboard, authenticated(component(DashboardPage))),
  entry(R.CurrentUser, authenticated(component(CurrentUserScene))),

  entry(R.UserList, requiresRole(component(UserSearchScene), ['Administrator'])),
  entry(R.UserView, requiresRole(componentWithId(UserViewScene), ['Administrator'])),
  entry(R.UserAdd, requiresRole(component(UserAddScene), ['Administrator'])),
  entry(R.UserEdit, requiresRole(componentWithId(UserEditScene), ['Administrator'])),

  entry(R.X1LanguageList, requiresRole(component(LanguageListComponent), ['ContentManager', 'Teacher'])),
  entry(R.X1Language, requiresRole(componentWithId(X1LanguageComponent), ['ContentManager', 'Teacher'])),
  entry(R.X2AgeCategory, requiresRole(componentWithId(X2AgeCategoryComponent), ['ContentManager', 'Teacher'])),
  entry(R.X3Level, requiresRole(componentWithId(X3LevelComponent), ['ContentManager', 'Teacher'])),
  entry(R.X4Unit, requiresRole(componentWithId(X4UnitComponent), ['ContentManager', 'Teacher'])),
  entry(R.X5Lesson, requiresRole(componentWithId(X5LessonComponent), ['ContentManager', 'Teacher'])),
  entry(R.X6Page, requiresRole(componentWithId(X6PageComponent), ['ContentManager', 'Teacher'])),
  entry(R.X7PushableBlock, requiresRole(componentWithId(X7PushableBlockComponent), ['ContentManager', 'Teacher'])),

  entry(R.AdminClassrooms, requiresRole(component(AdminClassroomScene), ['Administrator'])),
  entry(R.ClassroomDetails, requiresRole(componentWithId(ClassroomDetailsScene), ['Administrator'])),

  entry(R.TeacherSchedule, requiresRole(component(TeacherScheduleScene), ['Teacher'])),
  entry(R.TeacherClassroom, requiresRole(componentWithId(TeacherClassroomScene), ['Teacher'])),
  entry(R.TeacherWorkDays, requiresRole(component(TeacherWorkDayScene), ['Teacher'])),
  entry(R.TeacherLessons, requiresRole(component(TeacherLessonsScene), ['Teacher'])),
  entry(R.TeacherStudents, requiresRole(component(TeacherStudentsScene), ['Teacher'])),
  entry(R.EssayTeacherList, requiresRole(component(EssayTeacherListScene), ['Teacher'])),
  entry(R.EssayTeacher, requiresRole(componentWithId(EssayTeacherScene), ['Teacher'])),
  entry(R.ExamTeacherList, requiresRole(component(ExamTeacherListScene), ['Teacher'])),
  entry(R.ExamTeacher, requiresRole(componentWithId(ExamTeacherScene), ['Teacher'])),

  entry(R.StudentSchedule, requiresRole(component(StudentScheduleScene), ['Student'])),
  entry(R.StudentClassroom, requiresRole(componentWithId(StudentClassroomScene), ['Student'])),
  entry(R.StudentHomework, requiresRole(componentWithId(StudentHomeworkScene), ['Student'])),
  entry(R.StudentHomeworkList, requiresRole(component(StudentHomeworkListScene), ['Student'])),
  entry(R.EssayStudentList, requiresRole(component(EssayStudentListScene), ['Student'])),
  entry(R.EssayStudent, requiresRole(componentWithId(EssayStudentScene), ['Student'])),
  entry(R.ExamStudentList, requiresRole(component(ExamStudentListScene), ['Student'])),
  entry(R.ExamStudent, requiresRole(componentWithId(ExamStudentkScene), ['Student'])),


  entry(R.TimeZoneList, requiresRole(component(TimeZoneComponent), ['SystemAdministrator'])),
  entry(R.Test, component(TestScene)),
  entry(R.Welcome, component(Welcome)),

  // Registration
  entry(R.RegistrationList, requiresRole(component(RegistrationListScene), ['UserManager'])),

  // Profile
  entry(R.StudentProfile, requiresRole(componentWithId(StudentProfile), ['Student'])),
];
